/**
 * A stock's cycle reading as a row of figures: what the Stock Screener's
 * "Cycles" columns show and filter on, from the market scan the scanner
 * worker stores (`./scanStore.ts`).
 *
 * Null is not measured (the stock was not read: a fund too young, a name
 * that does not trade), never "no cycle": a stock that was read and has no
 * cycle that counts carries 0 in `cyc_found` and null in the rest. The weekly
 * columns (`cycw_*`) are the same figures of its weekly reading, renamed by
 * `weeklyOf`, so the two can never mean different things.
 *
 * Pure: the Screener's row type (and so the browser) imports this module.
 */
import type { StoredReading } from "./scanStore";

export type CycleColumns = {
  /** 1 when a cycle counts (FSC's Bartels test and a chance under 5%), 0 when none does. */
  cyc_found: number | null;
  /** The dominant cycle's length, bars. */
  cyc_len: number | null;
  /** Its half swing, % of price. */
  cyc_amp: number | null;
  /** Its push on the price, % a bar (amplitude over length). */
  cyc_strength: number | null;
  /** FSC's Bartels genuine %. */
  cyc_genuine: number | null;
  /** Chance %: how often versions of the stock's own moves showed a cycle as consistent. */
  cyc_chance: number | null;
  /** Its phase at the last close, degrees: 0 its crest, 180 its trough. */
  cyc_phase: number | null;
  /** 1 rising (between a trough and the crest after it), 0 falling. */
  cyc_rising: number | null;
  /** Bars to its next trough and next crest. */
  cyc_to_low: number | null;
  cyc_to_high: number | null;
  /** The composite's next turn: bars ahead, and 1 a low, -1 a high. */
  cyc_turn_in: number | null;
  cyc_turn: number | null;
  /** Cycles that count. */
  cyc_count: number | null;
};

export const CYCLE_COLUMN_IDS = [
  "cyc_found",
  "cyc_len",
  "cyc_amp",
  "cyc_strength",
  "cyc_genuine",
  "cyc_chance",
  "cyc_phase",
  "cyc_rising",
  "cyc_to_low",
  "cyc_to_high",
  "cyc_turn_in",
  "cyc_turn",
  "cyc_count",
] as const satisfies ReadonlyArray<keyof CycleColumns>;

export type WeeklyCycleColumns = { [K in keyof CycleColumns as K extends `cyc_${infer R}` ? `cycw_${R}` : never]: CycleColumns[K] };

export const EMPTY_CYCLE_COLUMNS: CycleColumns = Object.fromEntries(CYCLE_COLUMN_IDS.map((k) => [k, null])) as CycleColumns;
export const EMPTY_WEEKLY_CYCLE_COLUMNS: WeeklyCycleColumns = weeklyOf(EMPTY_CYCLE_COLUMNS);

/** The columns of a reading; a stock not read is `EMPTY_CYCLE_COLUMNS`. */
export function columnsOf(r: StoredReading | null | undefined): CycleColumns {
  if (!r) return EMPTY_CYCLE_COLUMNS;
  if (r.len == null) return { ...EMPTY_CYCLE_COLUMNS, cyc_found: 0, cyc_count: 0 };
  return {
    cyc_found: 1,
    cyc_len: r.len,
    cyc_amp: r.amp ?? null,
    cyc_strength: r.str ?? null,
    cyc_genuine: r.gen ?? null,
    cyc_chance: r.ch ?? null,
    cyc_phase: r.ph ?? null,
    cyc_rising: r.up ?? null,
    cyc_to_low: r.tl ?? null,
    cyc_to_high: r.th ?? null,
    cyc_turn_in: r.ta ?? null,
    cyc_turn: r.tk ?? null,
    cyc_count: r.k,
  };
}

/** The same figures under the weekly names. */
export function weeklyOf(c: CycleColumns): WeeklyCycleColumns {
  const out: Record<string, number | null> = {};
  for (const k of CYCLE_COLUMN_IDS) out[`cycw_${k.slice(4)}`] = c[k];
  return out as WeeklyCycleColumns;
}

/** Words for a turn value. */
export const CYCLE_TURN_CHOICES: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: "A low" },
  { value: -1, label: "A high" },
];
